import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';

import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';

import {
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@material-ui/core';

import Modal from './Modal';
import helpers from 'app/utils/helpers';
import notification from 'app/config/notification';
import { clearFiles } from 'app/store/slices/forms';
import { hideLoading, showLoading } from 'app/store/slices/settings';
import { updateRequestDetails } from 'app/store/slices/requests';
import { errorHandler } from 'app/utils/errorHandler';
import { STATUS_CODES } from 'app/config/status';
import { VEHICLE_REPORT_SITUATION } from 'app/utils/constants';

import certificationEndpoints from 'app/api/services/certificationEndpoints';
import requestEndpoints from 'app/api/services/requestEndpoints';
import filesEndpoint from 'app/api/services/filesEndpoint';
import surveyEndpoint from 'app/api/services/surveyEndpoint';

import styles from './../styles';
import { downloadFile } from '../../../../../utils/forms/files';
import { SERVICES_CODES } from '../../../../../config/services';

const useStyles = makeStyles(styles);

function VehicleCertification(props) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const { intl, validateDocuments, setNumCertificates } = props;

    const [certificationSelected, setCertificationSelected] = useState({});
    const [canAddNew, setCanAddNew] = useState(true);
    const [hasAll, setHasAll] = useState(false);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [updateFile, setUpdateFile] = useState(false);
    const [usedVehicles, setUsedVehicles] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const [sending, setSending] = useState(false);

    const [allCertificationChecked, setAllCertificationChecked] = useState(false);
    const [checkedCertification, setCheckedCertification] = useState([]);
    const [canSendToAssignCertificate, setCanSendToAssignCertificate] = useState(false);
    const showSendToAssignCertificate = false;

    const filesStore = useSelector((state) => state.forms.files);
    const request = useSelector((state) => state.requests.details);

    const requestJsonData = useMemo(() => {
        return request.jsonSolicitacao ? JSON.parse(request.jsonSolicitacao) : {};
    }, [request]);

    const canEdit = useMemo(() => {
        return request.situacao?.boEdicaoPortalInterno;
    }, [request]);

    const isSurvey = useMemo(() => {
        return SERVICES_CODES.VISTORIA === request.servico.cdServico;
    }, [request]);

    const [canEditCertificate, setCanEditCertificate] = useState(() => {
        return (
            canEdit &&
            (request.cdSituacao === STATUS_CODES.EM_ANALISE ||
                request.cdSituacao === STATUS_CODES.AGUARDANDO_AJUSTES_DOCUMENTO ||
                request.cdSituacao === STATUS_CODES.RETORNO_VISTORIADOR)
        );
    });

    useEffect(() => {
        if (
            canEdit &&
            checkedCertification.length > 0 &&
            checkedCertification.length === rows.length &&
            rows.length === vehicles.length
        ) {
            setCanSendToAssignCertificate(true);
        } else {
            setCanSendToAssignCertificate(false);
        }
    }, [canEdit, checkedCertification.length, rows.length, vehicles.length]);

    const getCertificates = useCallback(async () => {
        const response = await certificationEndpoints.getCertificates(request.cdSolicitacao);
        if (response.status === 200) {
            const certificatesRows = response.data;
            setRows(certificatesRows);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request.cdSolicitacao]);

    useEffect(() => {
        if (rows.length === vehicles.length) {
            setCanAddNew(false);
            if (rows.length > 0) {
                setHasAll(true);
            }
        } else {
            setCanAddNew(true);
            setHasAll(false);
        }
        setNumCertificates(vehicles.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, vehicles]);

    useEffect(() => {
        const init = async () => {
            if (rows.length > 0) {
                const temp = [...usedVehicles];
                rows.forEach((row) => {
                    if (vehicles) {
                        const filted = vehicles.filter((v) => v.cdVeiculo === row.cdVeiculo);
                        if (filted[0]?.cdVeiculo && !temp.includes(filted[0].cdVeiculo)) {
                            temp.push(filted[0].cdVeiculo);
                        }
                    }
                });
                setUsedVehicles(temp);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, selectedSurvey]);

    useEffect(() => {
        dispatch(clearFiles());
        const init = async () => {
            setLoading(true);
            try {
                const response = await surveyEndpoint.getSurveyByRequest(request.cdSolicitacao);
                if (response?.status === 200 && response.data) {
                    const survey = response.data;
                    if (survey.vistoriaVeiculos) {
                        const onlyApprovedOrNew = survey.vistoriaVeiculos.filter(
                            (vv) => (vv.tpSituacao === VEHICLE_REPORT_SITUATION.APROVADO.sg) || (vv.boVeiculoNovo === true),
                        );
                        setVehicles(onlyApprovedOrNew);
                    }
                    setSelectedSurvey(survey);
                }
                await getCertificates();
            } catch (error) {
                console.error(error);
                notification(intl.formatMessage({ id: 'MESSAGES.CERTIFICATION.PROBLEM_GET_CERTIFICATES' }), {
                    toastId: 'PROBLEM_GET_CERTIFICATES',
                    type: 'error',
                });
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            setLoading(false);
        };
        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, getCertificates]);

    const handleClose = () => {
        setOpen(false);
        setUpdateFile(false);
        dispatch(clearFiles());
        setCertificationSelected(null);
    };

    const openModal = (row) => {
        const certificateRow = { ...row, updateFile: false };
        setCertificationSelected(certificateRow);
        setOpen(true);
    };

    const onSave = async (values) => {
        if (!canEdit) return;
        try {
            setSending(true);
            const sendingData = new FormData();
            if (values.cdCertificado) sendingData.append('cdCertificado', values.cdCertificado);
            if (values.cdVeiculo) sendingData.append('cdVeiculo', values.cdVeiculo);
            if (values.idFileCrc) sendingData.append('idFileCrc', values.idFileCrc);
            if (values.idGdoc) sendingData.append('idGdoc', values.idGdoc);
            sendingData.append('cdSolicitacao', request.cdSolicitacao);
            if (requestJsonData.cdTipoEmpresa) {
                sendingData.append('cdTipoEmpresa', requestJsonData.cdTipoEmpresa);
            }
            if (requestJsonData.cdTipoPessoaFisica) {
                sendingData.append('cdTipoPessoaFisica', requestJsonData.cdTipoPessoaFisica);
            }
            if (values.deObservacao) {
                sendingData.append('deObservacao', values.deObservacao);
            }

            sendingData.append('dtEmissaoCrc', `${moment.utc(values.dtEmissaoCrc).format('YYYY-MM-DD')} 12:00:00`);
            sendingData.append(
                'dtVencimentoCrc',
                values.dtVencimentoCrc ? `${moment.utc(values.dtVencimentoCrc).format('YYYY-MM-DD')} 12:00:00` : '',
            );
            if (filesStore[0]) {
                const convertedFile = helpers.dataURItoBlob(filesStore[0].file);
                const fileName = filesStore[0].fileName ? filesStore[0].fileName : filesStore[0].name;
                sendingData.append('file', convertedFile, fileName);
            } else {
                try {
                    if (values.idFileCrc && !values.updateFile) {
                        const fileData = await filesEndpoint.getFileData(values.idFileCrc);
                        if (fileData?.status === 200 && fileData?.data) {
                            const responseBlob = await filesEndpoint.getFileBlob(values.idFileCrc);
                            if (responseBlob?.status === 200 && responseBlob?.data) {
                                const blob = new Blob([responseBlob.data], { type: 'application/pdf' });
                                sendingData.append('file', blob, fileData.data.name);
                            }
                        }
                    }
                } catch (error) {}
            }
            const response = await certificationEndpoints.saveCertificate(sendingData);
            if (response.status === 200) {
                notification(intl.formatMessage({ id: 'MESSAGES.CERTIFICATION.SUCCESSFUL_SAVED' }), {
                    toastId: 'SUCCESSFUL_SAVED',
                    type: 'success',
                });
                await getCertificates();
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        handleClose();
        setSending(false);
    };

    const sendToAssign = async () => {
        const documentsOK = await validateDocuments();
        if (!documentsOK) {
            notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.MISSING_DOCUMENTS_TO_APPROVE' }), {
                toastId: 'MISSING_DOCUMENTS_TO_APPROVE',
                type: 'warning',
            });
            dispatch(hideLoading());
            return;
        }

        try {
            dispatch(showLoading());
            const data = {
                cdSolicitacao: request.cdSolicitacao,
                certificados: [],
            };

            checkedCertification.forEach((c) => {
                data.certificados.push(c.cdCertificado);
            });

            if (data.certificados.length > 0) {
                const response = await certificationEndpoints.signListCertificate(data);
                if (response.status === 200) {
                    notification(intl.formatMessage({ id: 'MESSAGES.CERTIFICATION.SUCCESSFUL_SEND_SIGNATURE' }), {
                        toastId: 'SUCCESSFUL_SEND_SIGNATURE',
                        type: 'success',
                    });
                }
                setCanEditCertificate(false);
                setCanSendToAssignCertificate(false);
                getCertificates();
                const responseRequest = await requestEndpoints.findRequest(
                    { cdSolicitacao: request.cdSolicitacao },
                    '',
                );
                if (responseRequest.status === 200 && responseRequest.data) {
                    await dispatch(updateRequestDetails(responseRequest.data));
                }
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        dispatch(hideLoading());
    };

    const downloadFile_ = async (idFileCrc) => {
        await downloadFile({ idFile: idFileCrc, dispatch }, null);
    };
    const checkAll = (e) => {
        let key = 'cdCertificado';
        let newChecked = [...checkedCertification];
        rows.forEach((elem) => {
            if (e?.target?.checked) {
                setAllCertificationChecked(true);
                const element = _.find(checkedCertification, [key, elem[key]]);
                if (element) return;
                newChecked.push(elem);
            }
        });
        if (!e?.target?.checked) {
            newChecked = [];
            setAllCertificationChecked(false);
        }
        setCheckedCertification(newChecked);
    };

    const handleToggleCheckbox = (row) => {
        const currentIndex = checkedCertification.indexOf(row);
        const newChecked = [...checkedCertification];
        if (currentIndex === -1) {
            newChecked.push(row);
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setCheckedCertification(newChecked);
    };

    const renderListCertification = () => {
        return rows.map((row, index) => {
            let plate = '';
            if (vehicles) {
                const filted = vehicles.filter((vv) => vv.cdVeiculo === row.cdVeiculo);
                if (filted[0]?.veiculo?.dePlaca) {
                    plate = filted[0].veiculo?.dePlaca;
                }
            }
            let certificationStatus = 'Aguardando envio para assinatura';
            if (row.boAssinado) {
                certificationStatus = 'Documento assinado';
            } else {
                if (request.cdSituacao === STATUS_CODES.AGUARDANDO_ASSINATURA_DOCUMENTO) {
                    certificationStatus = 'Aguardando assinatura';
                }
            }
            let certificationNumber = String(row.cdCertificado).padStart(9, '0');
            if (row.numeroVistoria) {
                certificationNumber = String(row.numeroVistoria).padStart(9, '0');
            }
            return (
                <TableRow key={`cert-${index}`} className={classes.tableRow}>
                    {!isSurvey && (
                      <TableCell align="center">
                        <Checkbox
                          edge="start"
                          checked={checkedCertification.indexOf(row) !== -1}
                          tabIndex={-1}
                          disableRipple
                          disabled={!canEdit}
                          onClick={() => handleToggleCheckbox(row)}
                          inputProps={{'aria-labelledby': `cert-${index}`}}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center">{certificationNumber}</TableCell>
                    <TableCell align="center">{moment.utc(row.dtSolicitacao).format('DD/MM/YYYY')}</TableCell>
                    <TableCell align="left">{request?.servico?.nmServico}</TableCell>
                    <TableCell align="left">{plate}</TableCell>
                    {!isSurvey && (
                      <TableCell align="right">
                        <div className={classes.msgSignature}>
                            <img
                              alt=""
                              src={process.env.PUBLIC_URL + '/img/signature.png'}
                              style={{height: '30px'}}
                            />
                            {certificationStatus}
                        </div>
                      </TableCell>
                    )}
                    <TableCell align="right">
                        {row.idFileCrc && (
                            <Button
                                variant="contained"
                                color="secondary"
                                className={classes.btnSigleAction}
                                size="small"
                                onClick={() => downloadFile_(row.idFileCrc)}>
                                <PictureAsPdfIcon fontSize="inherit" />
                            </Button>
                        )}
                        {canEditCertificate && (
                            <Button
                                variant="contained"
                                color="primary"
                                className={classes.btnSigleAction}
                                size="small"
                                onClick={() => openModal(row)}>
                                <EditIcon fontSize="inherit" />
                            </Button>
                        )}
                    </TableCell>
                </TableRow>
            );
        });
    };

    return (
        <>
            <Modal
                {...props}
                classes={classes}
                open={open}
                onClose={handleClose}
                onSave={onSave}
                certification={certificationSelected}
                request={request}
                requestJsonData={requestJsonData}
                downloadFile={downloadFile_}
                updateFile={updateFile}
                setUpdateFile={setUpdateFile}
                vehicles={vehicles}
                usedVehicles={usedVehicles}
            />
            {loading && (
                <div className={classes.centerProgress}>
                    <CircularProgress size={20} />
                </div>
            )}
            {!loading && (
                <div className={classes.reponsiveTable}>
                    <Table className={classes.table} aria-label="customized table">
                        <TableHead>
                            <TableRow className={classes.tableRow}>
                                {!isSurvey && (
                                  <TableCell className={classes.headTableCell} align="center">
                                    <FormControlLabel
                                      className={classes.filterLabel}
                                      onClick={(event) => {
                                          event.stopPropagation();
                                          checkAll(event);
                                      }}
                                      disabled={!canEdit}
                                      onFocus={(event) => event.stopPropagation()}
                                      control={<Checkbox color="default" checked={allCertificationChecked}/>}
                                      label=""
                                    />
                                  </TableCell>
                                )}
                                <TableCell className={classes.headTableCell} align="center">
                                    Número
                                </TableCell>
                                <TableCell className={classes.headTableCell} align="center">
                                    Data Solicitação
                                </TableCell>
                                <TableCell className={classes.headTableCell} align="left">
                                    Tipo
                                </TableCell>
                                <TableCell className={classes.headTableCell} align="left">
                                    Placa
                                </TableCell>
                                {!isSurvey && (
                                  <TableCell className={classes.headTableCell} align="left" style={{minWidth: '295px'}}>
                                    Situação do certificado
                                  </TableCell>
                                )}
                                <TableCell className={classes.headTableCell} align="left" style={{ minWidth: '110px' }}>
                                    {!sending && canEditCertificate && canAddNew && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disableElevation
                                            className={classes.addButton}
                                            onClick={() => openModal(null)}>
                                            <AddIcon /> Adicionar
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>{renderListCertification()}</TableBody>
                    </Table>
                    {hasAll && showSendToAssignCertificate && canSendToAssignCertificate && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Button size="small" className={classes.btnSignature} onClick={() => sendToAssign()}>
                                    <img
                                        alt=""
                                        src={process.env.PUBLIC_URL + '/img/signature.png'}
                                        style={{ height: '30px' }}
                                    />
                                    Enviar para assinatura
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </div>
            )}
        </>
    );
}

export default injectIntl(VehicleCertification);
